import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle, XCircle, Loader2, ArrowLeft, ShoppingBag } from "lucide-react";
import { Button } from "./components/ui/button";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_equestrian-hub-20/artifacts/0lyoa6pm_No%20hatter%2C%20feher.png";

export function PaymentSuccess() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [orderNumber, setOrderNumber] = useState(null);
  
  const sessionId = searchParams.get("session_id");
  const orderParam = searchParams.get("order");
  
  useEffect(() => {
    const checkPayment = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }
      
      try {
        const response = await axios.get(`${API}/payments/status/${sessionId}`);
        if (response.data.payment_status === "paid") {
          setStatus("success");
          setOrderNumber(response.data.order_number || orderParam);
        } else if (response.data.payment_status === "pending") {
          // Poll again in 2 seconds
          setTimeout(checkPayment, 2000);
        } else {
          setStatus("error");
        }
      } catch (err) {
        setStatus("error");
      }
    };
    
    checkPayment();
  }, [sessionId, orderParam]);
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      <img src={LOGO_URL} alt="Gepárd Logo" className="w-24 h-24 mb-8 opacity-80" />
      
      {status === "loading" && (
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-indigo-400 animate-spin mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Fizetés ellenőrzése...</h1>
          <p className="text-slate-400">Kérlek várj, ellenőrizzük a fizetést.</p>
        </div>
      )}
      
      {status === "success" && (
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-12 h-12 text-emerald-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Sikeres fizetés!</h1>
          <p className="text-slate-400 mb-6">
            Köszönjük a rendelésedet! A fizetés sikeresen megtörtént.
            {orderNumber && (
              <span className="block mt-2 text-indigo-400 font-medium">
                Rendelésszám: {orderNumber}
              </span>
            )}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {orderNumber && (
              <Button
                onClick={() => navigate(`/order-tracking/${orderNumber}`)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <ShoppingBag className="w-4 h-4 mr-2" />
                Rendelés követése
              </Button>
            )}
            <Button
              onClick={() => navigate("/shop")}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Vissza a boltba
            </Button>
          </div>
        </div>
      )}
      
      {status === "error" && (
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-12 h-12 text-red-400" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Fizetési hiba</h1>
          <p className="text-slate-400 mb-6">
            Sajnos a fizetés nem sikerült vagy nem található. 
            Kérjük, próbáld újra vagy vedd fel velünk a kapcsolatot.
          </p>
          
          <Button
            onClick={() => navigate("/shop")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Vissza a boltba
          </Button>
        </div>
      )}
    </div>
  );
}

export function PaymentCancel() {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col items-center justify-center p-4">
      <img src={LOGO_URL} alt="Gepárd Logo" className="w-24 h-24 mb-8 opacity-80" />
      
      <div className="text-center max-w-md">
        <div className="w-20 h-20 bg-amber-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-12 h-12 text-amber-400" />
        </div>
        <h1 className="text-3xl font-bold text-white mb-2">Fizetés megszakítva</h1>
        <p className="text-slate-400 mb-6">
          A fizetés megszakításra került. A rendelésed nem került feldolgozásra, 
          a kártyádat nem terheltük meg.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Button
            onClick={() => navigate("/shop")}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <ShoppingBag className="w-4 h-4 mr-2" />
            Vissza a boltba
          </Button>
        </div>
      </div>
    </div>
  );
}
